import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Avatar from "../components/Avatar";
import { API } from "../api";

function PlanningGlobal() {
  const { token } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [tts, setTts] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [monthOffset, setMonthOffset] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [feries, setFeries] = useState({});

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    Promise.all([
      axios.get(`${API}/leaves/cabinet`, { headers }),
      axios.get(`${API}/users/cabinet`, { headers }),
      axios.get(`${API}/tt/cabinet`, { headers })
    ]).then(([leavesRes, usersRes, ttRes]) => {
      setLeaves(leavesRes.data);
      setTts(ttRes.data);
      setMembers(usersRes.data.map(u => ({ id: u.id, nom: u.nom, prenom: u.prenom, role: u.role })));
    }).catch(err => console.error(err));
  }, [token]);

  const today = new Date();
  const todayStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");

  const currentDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    axios.get(`${API}/feries/${currentYear}`, { headers }).then(r => {
      const map = {};
      r.data.forEach(f => { map[f.date] = f.name; });
      setFeries(map);
    }).catch(() => {});
  }, [token, currentYear]);
  const currentMonth = currentDate.getMonth();
  const monthName = currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(currentYear, currentMonth, i + 1);
    const str = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    return { date: d, str, num: i + 1, dayName: d.toLocaleDateString("fr-FR", { weekday: "short" }).slice(0, 2) };
  });

  const getLeave = (userId, dateStr) =>
    leaves.find(l => l.user_id === userId && l.date_debut.substring(0, 10) <= dateStr && l.date_fin.substring(0, 10) >= dateStr);

  const getTT = (userId, dateStr) =>
    tts.find(t => t.user_id === userId && t.date_debut.substring(0, 10) <= dateStr && t.date_fin.substring(0, 10) >= dateStr);

  const isWeekend = d => d.getDay() === 0 || d.getDay() === 6;

  const halfBg = (periode) => {
    if (periode === "apres-midi") return "linear-gradient(to top left,#B5D4F4 50%,#fff 50%)";
    return "linear-gradient(to bottom right,#B5D4F4 50%,#fff 50%)"; // matin (défaut)
  };

  const getCellStyle = (leave, tt, isWknd, isFerieDay, isToday, dateStr) => {
    let style;
    if (isWknd || isFerieDay) {
      style = { background: "#f5f5f7", borderColor: "#ebebeb" };
    } else if (leave && leave.type === "Absence") {
      if (leave.periode === "matin") style = { background: "linear-gradient(to bottom right,#F5D279 50%,#fff 50%)", borderColor: "#e0b948" };
      else if (leave.periode === "apres-midi") style = { background: "linear-gradient(to top left,#F5D279 50%,#fff 50%)", borderColor: "#e0b948" };
      else style = { background: "#F5D279", borderColor: "#e0b948" };
    } else if (leave && leave.statut === "approved") {
      const jours = parseFloat(leave.jours);
      const hasHalf = jours % 1 !== 0;
      const isLastDay = dateStr === leave.date_fin.substring(0, 10);
      const isFirstDay = dateStr === leave.date_debut.substring(0, 10);
      if (jours === 0.5) {
        style = { background: halfBg(leave.periode || "matin"), borderColor: "#9dbfe8" };
      } else if (hasHalf && isLastDay) {
        style = { background: halfBg("matin"), borderColor: "#9dbfe8" };
      } else if (hasHalf && isFirstDay && leave.periode === "apres-midi") {
        style = { background: halfBg("apres-midi"), borderColor: "#9dbfe8" };
      } else {
        style = { background: "#B5D4F4", borderColor: "#9dbfe8" };
      }
    } else if (leave && leave.statut === "pending") {
      style = { background: "repeating-linear-gradient(45deg,#dde8f5,#dde8f5 2px,#f8fbff 2px,#f8fbff 7px)", borderColor: "#ccd9ec" };
    } else if (tt) {
      if (tt.periode === "matin") style = { background: "linear-gradient(to bottom right,#B8E6C8 50%,#fff 50%)", borderColor: "#9dd4b0" };
      else if (tt.periode === "apres-midi") style = { background: "linear-gradient(to top left,#B8E6C8 50%,#fff 50%)", borderColor: "#9dd4b0" };
      else style = { background: "#B8E6C8", borderColor: "#9dd4b0" };
    } else {
      style = { background: "#fff", borderColor: "#f0f0f0" };
    }
    // "Aujourd'hui" : contour violet qui ne masque pas le contenu (TT, congé...)
    if (isToday) {
      if (style.background === "#fff") style = { background: "#EDE9FF", borderColor: "#c5b8f8" };
      style = { ...style, outline: "2px solid #7C6FE0", outlineOffset: -2 };
    }
    return style;
  };

  const ROLES = [
    { key: "associe", label: "Associés", color: "#185FA5", bg: "#E6F1FB" },
    { key: "daf", label: "DAF", color: "#7C6FE0", bg: "#EEEDFE" },
    { key: "assistante", label: "Assistantes", color: "#854F0B", bg: "#FAEEDA" },
    { key: "juriste", label: "Juristes", color: "#B45309", bg: "#FEF3C7" },
    { key: "collab", label: "Collaborateurs", color: "#3B6D11", bg: "#EAF3DE" },
  ];

  const filteredMembers = search
    ? members.filter(m => (m.prenom + " " + m.nom).toLowerCase().includes(search.toLowerCase()))
    : members;

  const leaveTooltipText = (m, leave) => {
    if (leave.type === "Absence") {
      const pLabel = leave.periode === "matin" ? " (matin)" : leave.periode === "apres-midi" ? " (après-midi)" : "";
      return `${m.prenom} ${m.nom} — Jour non travaillé${pLabel} — ${new Date(leave.date_debut).toLocaleDateString('fr-FR')}`;
    }
    const j = parseFloat(leave.jours);
    let halfLabel = '';
    if (j === 0.5) halfLabel = ` — ${leave.periode === 'apres-midi' ? 'Après-midi' : 'Matin'}`;
    else if (j % 1 !== 0) halfLabel = ' (dernière ½j matin)';
    return `${m.prenom} ${m.nom} — ${leave.type} — ${new Date(leave.date_debut).toLocaleDateString('fr-FR')} → ${new Date(leave.date_fin).toLocaleDateString('fr-FR')} (${j}j${halfLabel}) — ${leave.statut === 'approved' ? 'Approuvé' : 'En attente'}`;
  };

  const exportCSV = () => {
    const rows = [['Collaborateur', 'Type', 'Début', 'Fin', 'Jours', 'Demi-journée', 'Statut']];
    members.forEach(m => {
      leaves.filter(l => l.user_id === m.id && l.statut === 'approved' && l.type !== 'Absence').forEach(l => {
        rows.push([
          `${m.prenom} ${m.nom}`, l.type,
          new Date(l.date_debut).toLocaleDateString('fr-FR'),
          new Date(l.date_fin).toLocaleDateString('fr-FR'),
          parseFloat(l.jours),
          parseFloat(l.jours) === 0.5 ? (l.periode || 'matin') : '',
          'Approuvé'
        ]);
      });
    });
    const csv = '﻿' + rows.map(r => r.join(';')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
      download: `conges_${currentYear}_${String(currentMonth + 1).padStart(2, '0')}.csv`
    });
    a.click();
  };

  const groupedMembers = ROLES.map(r => ({
    ...r,
    members: filteredMembers.filter(m => m.role === r.key)
  })).filter(g => g.members.length > 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>🏢 Planning cabinet</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..."
            style={{ padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 13, width: 180, outline: "none" }} />
          <button onClick={() => setMonthOffset(0)}
            style={{ padding: "6px 12px", background: monthOffset === 0 ? "#7C6FE0" : "#f0f0f0", color: monthOffset === 0 ? "#fff" : "#555", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
            Aujourd'hui
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "4px 8px" }}>
            <button onClick={() => setMonthOffset(o => o - 1)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#7C6FE0", padding: "0 4px", lineHeight: 1 }}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#333", minWidth: 160, textAlign: "center", textTransform: "capitalize" }}>{monthName}</span>
            <button onClick={() => setMonthOffset(o => o + 1)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#7C6FE0", padding: "0 4px", lineHeight: 1 }}>›</button>
          </div>
          <button onClick={exportCSV}
            style={{ padding: "6px 14px", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 500, color: "#555" }}>
            📥 Export CSV
          </button>
          <button onClick={() => window.print()}
            style={{ padding: "6px 14px", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 500, color: "#555" }}>
            🖨️ PDF
          </button>
        </div>
      </div>

      {/* Légende */}
      <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { bg: "#B5D4F4", label: "Congé approuvé" },
          { bg: "repeating-linear-gradient(45deg,#dde8f5,#dde8f5 2px,#f8fbff 2px,#f8fbff 7px)", label: "En attente", border: "1px solid #ccd9ec" },
          { bg: "linear-gradient(to bottom right,#B5D4F4 50%,#fff 50%)", label: "½ j. matin", border: "1px solid #9dbfe8" },
          { bg: "linear-gradient(to top left,#B5D4F4 50%,#fff 50%)", label: "½ j. après-midi", border: "1px solid #9dbfe8" },
          { bg: "#B8E6C8", label: "Télétravail" },
          { bg: "linear-gradient(to bottom right,#B8E6C8 50%,#fff 50%)", label: "½ j. TT matin", border: "1px solid #9dd4b0" },
          { bg: "linear-gradient(to top left,#B8E6C8 50%,#fff 50%)", label: "½ j. TT après-midi", border: "1px solid #9dd4b0" },
          { bg: "#EDE9FF", label: "Aujourd'hui", border: "1px solid #c5b8f8" },
          { bg: "#f5f5f7", label: "Jour férié", border: "1px solid #ebebeb" },
          { bg: "#F5D279", label: "Jour non travaillé", border: "1px solid #e0b948" },
        ].map(({ bg, label, border }) => (
          <span key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#666" }}>
            <span style={{ width: 14, height: 14, background: bg, borderRadius: 3, display: "inline-block", border: border || "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}></span>
            {label}
          </span>
        ))}
      </div>

      {/* Vue liste mobile */}
      <div className="planning-list-view" style={{ display: "none" }}>
        {groupedMembers.map(group => (
          <div key={group.key} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: group.color, background: group.bg, padding: "4px 12px", borderRadius: 20, display: "inline-block", marginBottom: 8 }}>{group.label}</div>
            {group.members.map(m => {
              const myLeaves = leaves.filter(l =>
                l.user_id === m.id &&
                (l.statut === "approved" || l.statut === "pending") &&
                new Date(l.date_debut).getFullYear() === currentYear &&
                new Date(l.date_debut).getMonth() === currentMonth
              );
              return (
                <div key={m.id} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: "12px 14px", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: myLeaves.length > 0 ? 8 : 0 }}>
                    <Avatar prenom={m.prenom} nom={m.nom} size={28} fontSize={10} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{m.prenom} {m.nom}</span>
                  </div>
                  {myLeaves.length === 0
                    ? <div style={{ fontSize: 12, color: "#ccc" }}>Pas d'absence ce mois</div>
                    : myLeaves.map(l => (
                      <div key={l.id} style={{ background: l.statut === "approved" ? "#E6F1FB" : "#FAEEDA", borderRadius: 6, padding: "5px 10px", marginBottom: 4, fontSize: 12 }}>
                        {l.type} — {new Date(l.date_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} → {new Date(l.date_fin).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} ({parseFloat(l.jours)}j)
                        <span style={{ marginLeft: 8, fontSize: 10, color: l.statut === "approved" ? "#3B6D11" : "#854F0B" }}>{l.statut === "approved" ? "✅" : "⏳"}</span>
                      </div>
                    ))
                  }
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Grille desktop */}
      <div className="planning-table-view" style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} id="planning-print">
        <div className="print-only" style={{ padding: "10px 14px", fontSize: 15, fontWeight: 700, textTransform: "capitalize" }}>
          Planning cabinet — {monthName}
        </div>
        {filteredMembers.length === 0 && groupedMembers.length === 0 ? (
          <div style={{ padding: 32, fontSize: 13, color: "#aaa", textAlign: "center" }}>Aucun membre trouvé.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", width: "100%", minWidth: 680 }}>
              {/* En-tête des jours */}
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  <th style={{ width: 180, minWidth: 180, textAlign: "left", padding: "10px 14px", color: "#999", fontWeight: 500, fontSize: 11, borderBottom: "2px solid #eee", position: "sticky", left: 0, background: "#fafafa", zIndex: 2 }}>
                    Collaborateur
                  </th>
                  {days.map(d => {
                    const isWknd = isWeekend(d.date);
                    const isFerie = !!feries[d.str];
                    const isGray = isWknd || isFerie;
                    return (
                    <th key={d.str}
                      title={isFerie ? feries[d.str] : undefined}
                      style={{
                        width: 32, minWidth: 32, textAlign: "center", padding: "6px 1px",
                        borderBottom: "2px solid #eee",
                        background: d.str === todayStr ? "#EDE9FF" : isGray ? "#f5f5f7" : "#fafafa",
                        cursor: isFerie ? "help" : undefined,
                      }}>
                      <div style={{ fontSize: 9, color: isGray ? "#ccc" : d.str === todayStr ? "#7C6FE0" : "#bbb", fontWeight: 500 }}>{d.dayName}</div>
                      <div style={{ fontSize: 12, color: isGray ? "#ccc" : d.str === todayStr ? "#7C6FE0" : "#666", fontWeight: d.str === todayStr ? 700 : 500 }}>{d.num}</div>
                      {d.str === todayStr && <div style={{ width: 4, height: 4, background: "#7C6FE0", borderRadius: "50%", margin: "2px auto 0" }} />}
                    </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {groupedMembers.map(group => (
                  <React.Fragment key={group.key}>
                    {/* Séparateur de groupe */}
                    <tr>
                      <td colSpan={days.length + 1} style={{ padding: "8px 14px 4px", background: "#fafafa", borderTop: "1px solid #eee", borderBottom: "1px solid #eee" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: group.color, background: group.bg, padding: "2px 10px", borderRadius: 12, letterSpacing: "0.3px" }}>
                          {group.label}
                        </span>
                      </td>
                    </tr>
                    {group.members.map((m, idx) => (
                      <tr key={m.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fdfcff" }}>
                        <td style={{
                          padding: "5px 14px", whiteSpace: "nowrap",
                          borderBottom: "1px solid #f5f5f5",
                          position: "sticky", left: 0, zIndex: 1,
                          background: idx % 2 === 0 ? "#fff" : "#fdfcff",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Avatar prenom={m.prenom} nom={m.nom} size={26} fontSize={10} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{m.prenom} {m.nom}</span>
                          </div>
                        </td>
                        {days.map(d => {
                          const leave = getLeave(m.id, d.str);
                          const tt = !leave ? getTT(m.id, d.str) : null;
                          const isFerie = !!feries[d.str];
                          const cellStyle = getCellStyle(leave, tt, isWeekend(d.date), isFerie, d.str === todayStr, d.str);
                          return (
                            <td key={d.str}
                              onMouseEnter={e => {
                                if (leave) setTooltip({ x: e.clientX, y: e.clientY, text: leaveTooltipText(m, leave) });
                                else if (tt) {
                                const periodeLabel = tt.periode === "matin" ? " (matin)" : tt.periode === "apres-midi" ? " (après-midi)" : "";
                                setTooltip({ x: e.clientX, y: e.clientY, text: `${m.prenom} ${m.nom} — Télétravail${periodeLabel}` });
                              }
                                else if (isFerie) setTooltip({ x: e.clientX, y: e.clientY, text: `🗓️ ${feries[d.str]}` });
                              }}
                              onMouseLeave={() => setTooltip(null)}
                              onClick={() => { if (leave) { setTooltip(null); setSelectedLeave({ leave, member: m }); } }}
                              style={{
                                height: 32, width: 32,
                                ...cellStyle,
                                border: `1px solid ${cellStyle.borderColor}`,
                                borderRadius: 4,
                                cursor: leave ? 'pointer' : 'default',
                              }}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: "fixed", top: tooltip.y + 12, left: tooltip.x + 8,
          background: "rgba(30,30,40,0.92)", color: "#fff",
          padding: "6px 10px", borderRadius: 6, fontSize: 11, pointerEvents: "none",
          zIndex: 1000, maxWidth: 300, lineHeight: 1.4, boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
        }}>
          {tooltip.text}
        </div>
      )}

      {/* Modal détail congé */}
      {selectedLeave && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setSelectedLeave(null)}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 28, minWidth: 280, maxWidth: 360, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <Avatar prenom={selectedLeave.member.prenom} nom={selectedLeave.member.nom} size={34} fontSize={13} />
              <div style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>{selectedLeave.member.prenom} {selectedLeave.member.nom}</div>
            </div>
            {[
              { label: "Type", value: selectedLeave.leave.type },
              { label: "Début", value: new Date(selectedLeave.leave.date_debut).toLocaleDateString("fr-FR") },
              { label: "Fin", value: new Date(selectedLeave.leave.date_fin).toLocaleDateString("fr-FR") },
              { label: "Durée", value: `${parseFloat(selectedLeave.leave.jours)}j` },
              ...(parseFloat(selectedLeave.leave.jours) === 0.5 ? [{ label: "½ journée", value: selectedLeave.leave.periode === 'apres-midi' ? 'Après-midi' : 'Matin' }] : []),
              { label: "Statut", value: selectedLeave.leave.statut === "approved" ? "✅ Approuvé" : "⏳ En attente" },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ color: "#888" }}>{label}</span>
                <span style={{ fontWeight: 600, color: "#222" }}>{value}</span>
              </div>
            ))}
            <button style={{ marginTop: 18, width: "100%", background: "#7C6FE0", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              onClick={() => setSelectedLeave(null)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanningGlobal;
